import {CloseOutlined} from '@ant-design/icons';
import React, {useState, useEffect, useRef, useMemo} from 'react';
import {Editor, Transforms, Text as SlateText, Path} from 'slate';

import {
  User,
  Comment,
  CommentThread
} from '../../pages/reportBuilder/constants/ReportBuilderInterfce';
import {useEditorUI} from '../../pages/reportBuilder/editor-core/contexts/EditorUIContext';
import {
  encodeMentionsInText,
  formatMentionsForDisplay,
  renderTextWithMentions,
  type MentionMeta
} from '../../pages/reportBuilder/utils/commentMentionTokens';
import {notifyMentionedUsers} from '../../pages/reportBuilder/utils/commentNotifications';
import {
  canDeleteComment,
  canEditComment,
  canResolveComment
} from '../../pages/reportBuilder/utils/commentPermissions';
import {
  createCommentId,
  extractThreadsFromContent,
  findThreadBlockEntry,
  formatExactTime,
  formatRelativeTime,
  getCommentText,
  getCommentUserId,
  getThreadIdFromComment,
  isEmptyNewDraft,
  normalizeComment,
  removeEmptyDraft,
  scrollToCommentThread,
  truncateSnippet,
  type RawComment
} from '../../pages/reportBuilder/utils/commentUtils';
import {useCommentStore} from '../../store/reportStore/commentStore';
import {useReportUserStore} from '../../store/reportStore/reportUserStore';
import useUserStore from '../../store/userStore/userStore';

import '../../styles/_reportComponents.scss';

import {MentionList} from './MentionList';
import {useMentionPicker} from './useMentionPicker';

const getInitials = (name: string): string => {
  const safeName = name.trim();
  if (!safeName) return 'U';
  const parts = safeName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

// --- CommentThreadCard ---
interface CommentThreadCardProps {
  thread: Comment[];
  style?: React.CSSProperties;
  isActive: boolean;
  onClick: () => void;
  onClose?: () => void;
  onDelete: (id: string) => void;
  editor: Editor;
  element: any;
  path: Path;
  readOnly?: boolean;
}

export const CommentThreadCard: React.FC<CommentThreadCardProps> = ({
  thread,
  style,
  isActive,
  onClick,
  onClose,
  onDelete,
  editor,
  element,
  path,
  readOnly = false
}) => {
  const users = useReportUserStore((s) => s.users);
  const storeActiveUser = useReportUserStore((s) => s.activeUser);
  const loggedInUser = useUserStore((s) => s.userDetails);
  const activeUser: User = useMemo(() => {
    if (storeActiveUser?.id) return storeActiveUser;
    const firstName = loggedInUser?.firstName ?? '';
    const lastName = loggedInUser?.lastName ?? '';
    const fullName = `${firstName} ${lastName}`.trim();

    return {
      id: loggedInUser?._id ?? loggedInUser?.id ?? '',
      firstName,
      lastName,
      name: fullName || loggedInUser?.email || 'Unknown',
      avatar: getInitials(fullName || loggedInUser?.email || ''),
      color: '#5ba85a',
      email: loggedInUser?.email
    };
  }, [storeActiveUser, loggedInUser]);

  const [replyText, setReplyText] = useState('');
  const [replyMentions, setReplyMentions] = useState<MentionMeta[]>([]);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const replyMention = useMentionPicker();
  const {onCommentAdded} = useEditorUI();

  if (!thread || thread.length === 0) return null;

  const rootComment = thread[0];
  const isResolved = rootComment.resolved === true;
  const isComposingNewComment = thread.some(
    (c) => (c as RawComment).isNew === true
  );

  const handleReply = (e?: React.FormEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    if (!replyText.trim() || !editor || !path || readOnly) return;

    const encodedReplyText = encodeMentionsInText(replyText, replyMentions);
    const newReply: RawComment = {
      id: createCommentId(),
      threadId: rootComment.threadId ?? rootComment.id,
      text: encodedReplyText,
      userId: activeUser?.id ?? '',
      userFirstName: activeUser?.firstName,
      userLastName: activeUser?.lastName,
      mentions: replyMentions,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      type: 'replied'
    };
    const updatedComments = [...(element.comments || []), newReply];
    Transforms.setNodes(editor, {comments: updatedComments} as any, {at: path});

    notifyMentionedUsers({
      threadId: rootComment.threadId ?? rootComment.id,
      commentId: newReply.id,
      mentions: replyMentions,
      authorName: activeUser?.name,
      excerpt: encodedReplyText.slice(0, 120)
    });

    onCommentAdded();

    setReplyText('');
    setReplyMentions([]);
    replyMention.closeMentions();
  };

  const handleReplyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      replyMention.showMentions &&
      replyMention.mentionUsers.length > 0 &&
      (e.key === 'Enter' || e.key === 'Tab')
    ) {
      e.preventDefault();
      const selected =
        replyMention.mentionUsers[replyMention.mentionIndex] ??
        replyMention.mentionUsers[0];
      replyMention.handleMentionSelect(
        selected,
        replyText,
        setReplyText,
        setReplyMentions,
        replyTextareaRef
      );

      return;
    }
    replyMention.handleMentionKeyDown(e, () => {
      handleReply();
    });
  };

  return (
    <div
      className={`comment-thread-card${isActive ? ' active' : ''}${
        isResolved ? ' comment-thread-card--resolved' : ''
      }`}
      style={{...style, position: style?.position ?? 'absolute', cursor: 'pointer'}}
      onClick={onClick}
    >
      {isResolved && (
        <div className="comment-thread-card__resolved-banner">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
          </svg>
          Resolved
        </div>
      )}

      <div className="comment-thread-card__messages">
        {thread.map((comment, index) => (
          <CommentNode
            key={comment.id}
            comment={comment}
            users={users}
            activeUser={activeUser}
            isRoot={index === 0}
            editor={editor}
            element={element}
            path={path}
            onDelete={onDelete}
            onCloseThread={index === 0 ? onClose : undefined}
            readOnly={readOnly}
          />
        ))}
      </div>

      {!isResolved && !readOnly && !isComposingNewComment && (
        <div
          className="comment-thread-card__composer"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <textarea
            ref={replyTextareaRef}
            className="comment-thread-card__textarea"
            value={replyText}
            onChange={(e) => {
              replyMention.handleInputChange(e, setReplyText);
            }}
            placeholder="Reply to thread…"
            aria-label="Reply to comment thread"
            onKeyDown={handleReplyKeyDown}
          />
          <div className="comment-thread-card__btn-row">
            <button
              type="button"
              className="comment-btn comment-btn--primary"
              onClick={() => {
                handleReply();
              }}
              disabled={!replyText.trim()}
            >
              Reply
            </button>
          </div>
        </div>
      )}

      {replyMention.showMentions && (
        <MentionList
          users={replyMention.mentionUsers}
          onSelect={(u) => {
            replyMention.handleMentionSelect(
              u,
              replyText,
              setReplyText,
              setReplyMentions,
              replyTextareaRef
            );
          }}
          onClose={replyMention.closeMentions}
          position={replyMention.mentionPosition}
          activeIndex={replyMention.mentionIndex}
          onReachEnd={replyMention.loadMoreMentions}
          hasMore={replyMention.mentionHasMore}
          isLoading={replyMention.mentionLoading}
          hasSearched={replyMention.mentionHasSearched}
        />
      )}
    </div>
  );
};

// --- CommentNode ---
interface CommentNodeProps {
  comment: Comment;
  users: User[];
  activeUser: User | null;
  isRoot: boolean;
  editor: Editor;
  element: any;
  path: Path;
  onDelete?: (id: string) => void;
  onCloseThread?: () => void;
  readOnly?: boolean;
}

const CommentNode: React.FC<CommentNodeProps> = ({
  comment,
  users,
  activeUser,
  isRoot,
  editor,
  element,
  path,
  onDelete,
  onCloseThread,
  readOnly = false
}) => {
  const raw = comment as RawComment;
  const commentUserId = getCommentUserId(raw);
  const commentFirstName = String(raw.userFirstName ?? '').trim();
  const commentLastName = String(raw.userLastName ?? '').trim();
  const commentNameFromPayload = `${commentFirstName} ${commentLastName}`.trim();
  const matchedCommentUser = users.find((u) => u.id === commentUserId);
  const fallbackName = commentNameFromPayload || 'Unknown';
  const user: User =
    matchedCommentUser ??
    {
      id: commentUserId || 'unknown',
      name: fallbackName,
      avatar: getInitials(fallbackName),
      color: '#9AA0A6',
      firstName: commentFirstName || undefined,
      lastName: commentLastName || undefined
    };

  const resolvedCommentText = getCommentText(raw);
  const mentions = Array.isArray(raw.mentions) ? raw.mentions : [];
  const [isEditing, setIsEditing] = useState(comment.isNew === true);
  const [editText, setEditText] = useState(() =>
    formatMentionsForDisplay(resolvedCommentText, mentions, users)
  );
  const [editMentions, setEditMentions] = useState<MentionMeta[]>(mentions);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editMention = useMentionPicker();
  const setActiveCommentNodeId = useCommentStore(
    (s) => s.setActiveCommentNodeId
  );
  const {onCommentAdded} = useEditorUI();

  const allowEdit = !readOnly && canEditComment(raw, activeUser?.id);
  const allowDelete = !readOnly && canDeleteComment(raw, activeUser?.id);
  const allowResolve =
    isRoot && !readOnly && canResolveComment(raw, activeUser?.id);

  const handleSave = () => {
    if (!editText.trim() || !editor || !path) return;
    const encodedEditText = encodeMentionsInText(editText, editMentions);
    const updatedComments = element.comments.map((c: RawComment) =>
      c.id === comment.id
        ? {
            ...c,
            text: encodedEditText,
            comment: encodedEditText,
            isNew: false,
            mentions: editMentions,
            updatedAt: new Date().toISOString(),
            edited: {
              userId: activeUser?.id ?? 'unknown',
              userName: activeUser?.name ?? 'Unknown'
            }
          }
        : c
    );
    Transforms.setNodes(editor, {comments: updatedComments} as any, {at: path});
    notifyMentionedUsers({
      threadId: comment.threadId ?? comment.id,
      commentId: comment.id,
      mentions: editMentions,
      authorName: activeUser?.name,
      excerpt: encodedEditText.slice(0, 120)
    });
    onCommentAdded();
    setIsEditing(false);
    editMention.closeMentions();
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      editMention.showMentions &&
      editMention.mentionUsers.length > 0 &&
      (e.key === 'Enter' || e.key === 'Tab')
    ) {
      e.preventDefault();
      const selected =
        editMention.mentionUsers[editMention.mentionIndex] ??
        editMention.mentionUsers[0];
      editMention.handleMentionSelect(
        selected,
        editText,
        setEditText,
        setEditMentions,
        textareaRef
      );

    }
    editMention.handleMentionKeyDown(e, () => {
      handleSave();
    });
  };

  const handleCancel = (): void => {
    if (
      comment.isNew &&
      isEmptyNewDraft({...raw, text: editText, comment: editText})
    ) {
      const threadId = getThreadIdFromComment(raw);
      if (editor) {
        removeEmptyDraft(editor, threadId);
      }
      setActiveCommentNodeId(null);
    } else {
      setIsEditing(false);
    }
    editMention.closeMentions();
  };

  return (
    <div
      className={`comment-node${isRoot ? ' comment-node--root' : ' comment-node--reply'}`}
    >
      <div className="comment-node__header">
        <div
          className="comment-node__avatar"
          style={{backgroundColor: user.color}}
        >
          {user.avatar}
        </div>
        <div className="comment-node__meta">
          <div className="comment-node__title-row">
            <span className="comment-node__name">{user.name}</span>
            {!isEditing && comment.type !== 'resolved_event' && (
              <div className="comment-node__actions">
                {allowEdit && (
                  <button
                    type="button"
                    className="comment-node__action-btn"
                    aria-label="Edit comment"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditText(
                        formatMentionsForDisplay(
                          resolvedCommentText,
                          mentions,
                          users
                        )
                      );
                      setEditMentions(mentions);
                      setIsEditing(true);
                    }}
                  >
                    Edit
                  </button>
                )}
                {allowResolve && (
                  <button
                    type="button"
                    className="comment-node__action-btn comment-node__action-btn--resolve"
                    aria-label={
                      comment.resolved ? 'Reopen thread' : 'Resolve thread'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      const newResolvedState = !comment.resolved;
                      const threadTargetId = comment.threadId ?? comment.id;
                      Editor.withoutNormalizing(editor, () => {
                        Transforms.setNodes(
                          editor,
                          {commentThreadResolved: newResolvedState} as any,
                          {
                            at: [],
                            match: (n) =>
                              SlateText.isText(n) &&
                              n.commentThreadId === threadTargetId,
                            mode: 'all'
                          }
                        );
                      });
                      const updatedComments = element.comments.map(
                        (c: RawComment) =>
                          c.id === comment.id ||
                          getThreadIdFromComment(c) === threadTargetId
                            ? {
                                ...c,
                                resolved: newResolvedState,
                                updatedAt: new Date().toISOString()
                              }
                            : c
                      );
                      Transforms.setNodes(
                        editor,
                        {comments: updatedComments} as any,
                        {at: path}
                      );
                      onCommentAdded();
                    }}
                  >
                    {comment.resolved ? 'Reopen' : 'Resolve'}
                  </button>
                )}
                {allowDelete && (
                  <button
                    type="button"
                    className="comment-node__action-btn comment-node__action-btn--delete"
                    aria-label="Delete comment"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(comment.id);
                    }}
                  >
                    Delete
                  </button>
                )}
                {isRoot && onCloseThread && (
                  <button
                    type="button"
                    className="comment-node__action-btn"
                    aria-label="Close comment card"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseThread();
                    }}
                  >
                    <CloseOutlined />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="comment-node__time">
            <span title={formatExactTime(comment.createdAt)}>
              {formatRelativeTime(comment.createdAt)}
            </span>
            {comment.edited ? (
              <span className="comment-node__edited"> (edited)</span>
            ) : null}
          </div>
        </div>
      </div>

      {isEditing ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <textarea
            ref={textareaRef}
            className="comment-node__textarea"
            value={editText}
            onChange={(e) => {
              editMention.handleInputChange(e, setEditText);
            }}
            onKeyDown={handleEditKeyDown}
            autoFocus
            aria-label="Edit comment"
          />
          {editMention.showMentions && (
            <MentionList
              users={editMention.mentionUsers}
              onSelect={(u) => {
                editMention.handleMentionSelect(
                  u,
                  editText,
                  setEditText,
                  setEditMentions,
                  textareaRef
                );
              }}
              onClose={editMention.closeMentions}
              position={editMention.mentionPosition}
              activeIndex={editMention.mentionIndex}
              onReachEnd={editMention.loadMoreMentions}
              hasMore={editMention.mentionHasMore}
              isLoading={editMention.mentionLoading}
              hasSearched={editMention.mentionHasSearched}
            />
          )}
          <div className="comment-node__btn-row">
            <button
              type="button"
              className="comment-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="comment-btn comment-btn--primary"
              onClick={handleSave}
              disabled={!editText.trim()}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="comment-node__text">
          {renderTextWithMentions(resolvedCommentText, mentions, users) ??
            resolvedCommentText}
        </div>
      )}
    </div>
  );
};

// --- CommentList ---
interface CommentListProps {
  filter?: string;
  sort?: 'newest' | 'oldest';
  content?: any[];
  editor?: Editor;
}

export const CommentList: React.FC<CommentListProps> = ({
  filter = 'open',
  sort = 'newest',
  content: panelContent,
  editor
}) => {
  const content = panelContent ?? [];
  const activeCommentNodeId = useCommentStore((s) => s.activeCommentNodeId);
  const setActiveCommentNodeId = useCommentStore(
    (s) => s.setActiveCommentNodeId
  );
  const users = useReportUserStore((s) => s.users);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const {onCommentAdded} = useEditorUI();

  const filterKey =
    filter === 'resolved' ? 'resolved' : filter === 'open' ? 'open' : 'all';

  const threads = useMemo(
    () => extractThreadsFromContent(content, filterKey, sort),
    [content, filterKey, sort]
  );

  const activeThreadEntry = useMemo(() => {
    if (!editor || !activeCommentNodeId) return null;

    return findThreadBlockEntry(editor, activeCommentNodeId);
  }, [editor, activeCommentNodeId, content]);

  useEffect(() => {
    if (!activeCommentNodeId) return;
    const row = rowRefs.current.get(activeCommentNodeId);
    if (!row) return;

    requestAnimationFrame(() => {
      row.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    });
  }, [activeCommentNodeId, threads]);

  const activateThread = (threadId: string) => {
    if (activeCommentNodeId === threadId) {
      if (editor) removeEmptyDraft(editor, threadId);
      setActiveCommentNodeId(null);

      return;
    }

    setActiveCommentNodeId(threadId);
    requestAnimationFrame(() => {
      scrollToCommentThread(threadId);
    });
  };

  const collapseThread = (threadId: string) => {
    if (editor) removeEmptyDraft(editor, threadId);
    setActiveCommentNodeId(null);
  };

  const handleDeleteComment = (
    threadId: string,
    commentId: string,
    entry: [any, Path]
  ) => {
    if (!editor) return;
    const [el, p] = entry;
    const filtered = (
      (el as {comments?: RawComment[]}).comments ?? []
    ).filter((c) => c.id !== commentId);

    if (
      filtered.length === 0 ||
      !filtered.some(
        (c) => c.threadId === threadId || c.id === threadId
      )
    ) {
      Editor.withoutNormalizing(editor, () => {
        Transforms.unsetNodes(editor, 'commentThreadId', {
          at: [],
          match: (n) =>
            SlateText.isText(n) && n.commentThreadId === threadId,
          mode: 'all'
        });
      });
    }

    if (filtered.length === 0) {
      Transforms.setNodes(
        editor,
        {comments: undefined, commentId: undefined} as any,
        {at: p}
      );
      setActiveCommentNodeId(null);
    } else {
      Transforms.setNodes(editor, {comments: filtered} as any, {at: p});
    }

    onCommentAdded();
  };

  if (threads.length === 0) {
    return (
      <div className="comment-panel__empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="#d2d8e6">
          <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z" />
        </svg>
        <p>No comments yet</p>
        <p style={{fontSize: 12}}>Select text and add a comment to start a discussion.</p>
      </div>
    );
  }

  return (
    <div className="comment-panel__list">
      {threads.map((thread: CommentThread) => {
        const root = normalizeComment(thread.root as RawComment);
        const author: User =
          users.find((u) => u.id === root.userId) ?? {
            id: root.userId,
            name:
              `${root.userFirstName ?? ''} ${root.userLastName ?? ''}`.trim() ||
              'Unknown',
            avatar: getInitials(
              `${root.userFirstName ?? ''} ${root.userLastName ?? ''}`.trim()
            ),
            color: '#5ba85a'
          };
        const replyCount = thread.replies.length;
        const isExpanded = activeCommentNodeId === thread.threadId;
        const threadEntry =
          isExpanded && activeThreadEntry ? activeThreadEntry : null;
        const threadComments =
          threadEntry && editor
            ? (
                (threadEntry[0] as {comments?: RawComment[]}).comments ?? []
              ).filter(
                (c) =>
                  c.threadId === thread.threadId || c.id === thread.threadId
              )
            : [];

        return (
          <div
            key={thread.threadId}
            ref={(el) => {
              if (el) rowRefs.current.set(thread.threadId, el);
              else rowRefs.current.delete(thread.threadId);
            }}
            className={`comment-thread-row${
              thread.resolved ? ' comment-thread-row--resolved' : ''
            }${isExpanded ? ' comment-thread-row--expanded' : ''}`}
          >
            {isExpanded && editor && threadEntry && threadComments.length > 0 ? (
              <CommentThreadCard
                key={thread.threadId}
                thread={threadComments.map((c) => normalizeComment(c))}
                editor={editor}
                element={threadEntry[0]}
                path={threadEntry[1]}
                isActive
                style={{position: 'relative', width: '100%'}}
                onClick={() => {}}
                onClose={() => {
                  collapseThread(thread.threadId);
                }}
                onDelete={(id) => {
                  handleDeleteComment(thread.threadId, id, threadEntry);
                }}
              />
            ) : (
              <div
                className="comment-thread-row__header"
                onClick={() => {
                  activateThread(thread.threadId);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    activateThread(thread.threadId);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div
                  className="comment-thread-row__avatar"
                  style={{backgroundColor: author.color}}
                >
                  {author.avatar}
                </div>
                <div className="comment-thread-row__body">
                  <div className="comment-thread-row__meta">
                    <span className="comment-thread-row__author">
                      {author.name}
                    </span>
                    <span
                      className="comment-thread-row__time"
                      title={formatExactTime(root.createdAt)}
                    >
                      {formatRelativeTime(root.createdAt)}
                    </span>
                  </div>
                  {thread.snippet ? (
                    <div className="comment-thread-row__snippet">
                      &ldquo;{truncateSnippet(thread.snippet)}&rdquo;
                    </div>
                  ) : null}
                  <div className="comment-thread-row__preview">
                    {getCommentText(root as RawComment)}
                  </div>
                  <div className="comment-thread-row__footer">
                    <span
                      className={`comment-thread-row__badge${
                        thread.resolved
                          ? ' comment-thread-row__badge--resolved'
                          : ' comment-thread-row__badge--open'
                      }`}
                    >
                      {thread.resolved ? 'Resolved' : 'Open'}
                    </span>
                    {replyCount > 0 && (
                      <span className="comment-thread-row__replies">
                        {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// --- CommentPanel wrapper ---
export const CommentPanel: React.FC<{
  onClose?: () => void;
  content?: any[];
  editor?: Editor;
}> = ({onClose, content, editor}) => {
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('all');
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const users = useReportUserStore((s) => s.users);
  const fetchUsers = useReportUserStore((s) => s.fetchUsers);
  const threadCount = useMemo(() => {
    if (!content) return 0;

    return extractThreadsFromContent(content, 'all').length;
  }, [content]);

  useEffect(() => {
    if (!users.length) void fetchUsers();
  }, [fetchUsers, users.length]);

  return (
    <div className="comment-panel">
      <div className="comment-panel__header">
        <div className="comment-panel__header-row">
          <div className="comment-panel__title">Comments</div>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            {threadCount > 0 && (
              <span className="comment-panel__count">{threadCount}</span>
            )}
            {onClose && (
              <button
                type="button"
                className="comment-panel__close-btn"
                aria-label="Close comments panel"
                onClick={onClose}
              >
                <CloseOutlined />
              </button>
            )}
          </div>
        </div>
        <div className="comment-panel__filters">
          {(['all', 'open', 'resolved'] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={`comment-panel__filter-btn${filter === v ? ' active' : ''}`}
              onClick={() => {
                setFilter(v);
              }}
            >
              {v === 'all' ? 'All' : v === 'open' ? 'Open' : 'Resolved'}
            </button>
          ))}
        </div>
        <div className="comment-panel__sort">
          <button
            type="button"
            className={`comment-panel__sort-btn${sort === 'newest' ? ' active' : ''}`}
            onClick={() => {
              setSort('newest');
            }}
          >
            Newest
          </button>
          <button
            type="button"
            className={`comment-panel__sort-btn${sort === 'oldest' ? ' active' : ''}`}
            onClick={() => {
              setSort('oldest');
            }}
          >
            Oldest
          </button>
        </div>
      </div>
      <CommentList
        filter={filter}
        sort={sort}
        content={content}
        editor={editor}
      />
    </div>
  );
};
